'use client';

import { logger } from '@/lib/utils/logger';
import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { CatalogPage } from '@/components/ui/catalog/catalog-page';
import { SearchBarConfig } from '@/components/ui/catalog/search-bar';
import { useInfiniteScroll } from '@/lib/hooks/use-infinite-scroll';
import { PaginatedResponse } from '@/lib/services/catalog/catalog-types';
import { useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CatalogItem,
  FilterCategory,
  CatalogType,
  SelectedFiltersType,
} from '@/lib/services/catalog/catalog-types';
import { CatalogAPIProviders } from './catalog-api-providers';
import { uniqueCatalogItems } from './catalog-identity';
import { AnimatedLoading } from '../../animated-title';

// Type for view modes
type ViewMode = 'grid4' | 'grid6' | 'grid8' | 'table' | 'gallery';

interface CatalogClientProps {
  headerContent?: ReactNode;
  collectionKey?: string; // Geriye dönük uyumluluk için
  collectionKeys?: string[];
  initialData: PaginatedResponse<CatalogItem> | PaginatedResponse<CatalogItem>[] | null;
  searchConfig: SearchBarConfig;
  filterCategories: FilterCategory[];
  pageLimit: number;
  catalogType: CatalogType;
  collectionid?: number;
}

export function CatalogClient({
  headerContent,
  collectionKey,
  collectionKeys,
  initialData,
  searchConfig,
  filterCategories,
  pageLimit,
  catalogType,
  collectionid,
}: CatalogClientProps) {
  const searchParams = useSearchParams();
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | Window | null>(null);

  // URL'den başlangıç durumunu al
  const initialQuery = searchParams.get('query') || '';
  let initialSort: string;
  if (collectionKeys && collectionKeys.length > 0 && collectionKeys[0] === 'artworks') {
    initialSort = searchParams.get('sort') || 'rand_asc';
  } else {
    initialSort = searchParams.get('sort') || 'newest';
  }

  // catalog-client.tsx dosyasında şu şekilde değiştirebilirsiniz:
  const getInitialViewFromStorage = () => {
    if (typeof window !== 'undefined') {
      // Default to table view for INDEX page only (catalogType === 'all')
      if (catalogType === 'all') {
        return localStorage.getItem('viewMode-all') || 'table';
      }

      if (collectionKeys && collectionKeys.length > 0 && collectionKeys[0] === 'exhibitions') {
        let vm = localStorage.getItem('viewMode') || 'grid6';
        // logger.warn('Initial view mode:', vm);
        if (vm !== 'grid6' && vm !== 'table') {
          vm = 'grid6';
        }
        return vm;
      }

      return localStorage.getItem('viewMode') || 'grid6';
    }
    return 'grid6';
  };

  const initialView = searchParams.get('view') || getInitialViewFromStorage();

  // Client-side debugging
  const isClient = useRef(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      isClient.current = true;
    }
  }, []);

  // Scroll container'ını ayarla
  useEffect(() => {
    if (!isClient.current) return undefined;

    logger.debug('Scroll container ayarlanıyor...');

    // Sayfa tamamen yüklendikten sonra catalog-content elementini bul
    const setupScrollContainer = () => {
      // catalog-content elementini bul
      let container: HTMLElement | Window = document.querySelector('#catalog-scroll-container') as HTMLElement;

      // Eğer bulunamazsa, document.body'yi kullan
      if (!container) {
        logger.debug('catalog-scroll-container bulunamadı, window kullanılıyor');
        container = window;
      } else {
        logger.debug('catalog-scroll-container bulundu, container olarak kullanılıyor');
      }

      // Scroll container'ını ayarla
      setScrollContainer(container);

      // Debug: Container'ın boyutlarını kontrol et
      if (container instanceof HTMLElement) {
        logger.debug('Container boyutları:', {
          clientHeight: container.clientHeight,
          scrollHeight: container.scrollHeight,
          offsetHeight: container.offsetHeight,
        });
      } else if (container === window) {
        logger.debug('Window boyutları:', {
          innerHeight: window.innerHeight,
          outerHeight: window.outerHeight,
          documentHeight: document.documentElement.scrollHeight,
        });
      }
    };

    // İlk denemede bulamazsak, kısa bir gecikme ile tekrar deneyelim
    setupScrollContainer();

    // Eğer ilk denemede bulunamazsa, DOM tamamen yüklendikten sonra tekrar dene
    if (!scrollContainer) {
      const timeoutId = setTimeout(() => {
        setupScrollContainer();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [isClient, scrollContainer]);

  // QueryClientProvider içinde render et
  if (!scrollContainer) {
    // İlk render sırasında loading göster
    return <>{headerContent}<AnimatedLoading /></>;
  }

  return (
    <DataClientContent
      headerContent={headerContent}
      collectionKey={collectionKey}
      collectionKeys={collectionKeys}
      catalogType={catalogType}
      initialData={initialData}
      searchConfig={searchConfig}
      filterCategories={filterCategories}
      pageLimit={pageLimit}
      scrollContainer={scrollContainer}
      initialQuery={initialQuery}
      initialSort={initialSort}
      initialView={initialView}
      collectionid={collectionid}
    />
  );
}

// İç bileşen - React Query kullanımı için
function DataClientContent({
  headerContent,
  collectionKey,
  collectionKeys,
  catalogType,
  initialData,
  searchConfig,
  filterCategories,
  pageLimit,
  scrollContainer,
  initialQuery,
  initialSort,
  initialView,
  collectionid,
}: CatalogClientProps & {
  scrollContainer: HTMLElement | Window;
  initialQuery: string;
  initialSort: string;
  initialView: string;
}) {
  const queryClient = useQueryClient();
  // DataClientContent fonksiyonunun başında, diğer state tanımlamalarının yanına ekleyin
  const [scrollContainerState, setScrollContainer] = useState<HTMLElement | Window>(scrollContainer);
  // Filtreleme durumu
  const [searchValue, setSearchValue] = useState(initialQuery);
  const [sortOption, setSortOption] = useState(initialSort);
  const [viewMode, setViewMode] = useState<ViewMode>(initialView as ViewMode);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>({
    medium: [],
    category: [],
    chains: [],
    years: [],
    type: [],
    platform: [],
  });

  // Client-side debugging
  const isClient = useRef(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      isClient.current = true;
    }
  }, []);

  // DataClientContent içinde

  useEffect(() => {
    // viewMode gallery ise, gallery-wall-scroll-container'ı kullan
    if (viewMode === 'gallery') {
      const galleryContainer = document.querySelector('#gallery-wall-scroll-container') as HTMLElement;
      if (galleryContainer) {
        // The DOM scroll host is discovered after the view commits.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setScrollContainer(galleryContainer);
        logger.debug('Gallery scroll container bulundu ve ayarlandı');
      }
    } else {
      // Diğer viewMode'lar için normal catalog-scroll-container'ı kullan
      let container: HTMLElement | Window = document.querySelector('#catalog-scroll-container') as HTMLElement;
      if (!container) {
        logger.debug('catalog-scroll-container bulunamadı, window kullanılıyor');
        container = window;
      } else {
        logger.debug('catalog-scroll-container bulundu, container olarak kullanılıyor');
      }
      setScrollContainer(container);
    }
  }, [viewMode]);

  // Geriye dönük uyumluluk için collectionKeys'i normalize et
  const keys = useMemo(() => collectionKeys || (collectionKey ? [collectionKey] : []), [collectionKeys, collectionKey]);

  // Geriye dönük uyumluluk için initialData'yı normalize et
  const normalizedInitialData = Array.isArray(initialData) ? combineInitialData(initialData) : initialData;

  // Sonsuz kaydırma için useInfiniteScroll hook'unu kullan
  const infiniteQuery = useInfiniteScroll({
    // TODO: type properly - useInfiniteScroll queryKey expects (string | Record<string, unknown>)[]
    // viewMode is deliberately NOT part of the key: the fetch is identical in
    // every view, and keying on it made each toggle abandon the loaded pages
    // (the grid showed NO RESULTS until a full reload).
    queryKey: [keys, searchValue, sortOption, selectedFilters, collectionid] as unknown as (string | Record<string, unknown>)[],
    queryFn: async (params) => {
      const page = params.page as number | undefined;
      const limit = params.limit as number | undefined;
      // Client-side'da çalışan console.log
      if (isClient.current) {
        logger.debug(`Sayfa ${page}, limit ${limit} için veri yükleniyor...`);
      }

      const result = await CatalogAPIProviders({
        collectionKeys: keys,
        collectionid,
        page,
        limit,
        query: searchValue,
        sort: sortOption,
        selectedFilters,
      });

      // Client-side'da çalışan console.log
      if (isClient.current && result) {
        logger.debug(`Sayfa ${page} için API yanıtı:`, {
          totalDocs: result.totalDocs,
          totalPages: result.totalPages,
          page: result.page,
          hasNextPage: result.hasNextPage,
          docs: result.docs?.length,
        });
      }

      // TODO: type properly - useInfiniteScroll PageResult.docs is Record<string, unknown>[] but we have CatalogItem[]
      return (result || {
          docs: [],
          totalDocs: 0,
          limit: 0,
          totalPages: 0,
          page: 0,
          pagingCounter: 0,
          hasPrevPage: false,
          hasNextPage: false,
          prevPage: null,
          nextPage: null,
        }) as PaginatedResponse<CatalogItem> & { docs: Record<string, unknown>[] };
    },
    initialData: normalizedInitialData ? { ...normalizedInitialData, docs: normalizedInitialData.docs as unknown as Record<string, unknown>[] } : undefined,
    enabled: true,
    limit: pageLimit,
    scrollContainer: scrollContainerState,
    viewMode,
  });

  // Veri yükleniyor mu?
  const isLoading = infiniteQuery.isLoading;

  // Veri yüklenirken hata oluştu mu?
  // const isError = infiniteQuery.isError;

  // Tüm sayfalardan gelen verileri birleştir
  const rawItems = infiniteQuery.data?.pages.flatMap(page => page.docs || []) || normalizedInitialData?.docs || [];
  const allItems = uniqueCatalogItems<CatalogItem | Record<string, unknown>>(rawItems);

  // Toplam belge sayısını al
  const totalDocs = infiniteQuery.data?.pages[0]?.totalDocs || normalizedInitialData?.totalDocs || 0;

  // Debug: Query durumunu kontrol et
  useEffect(() => {
    if (isClient.current) {
      logger.debug('Query durumu:', {
        hasNextPage: infiniteQuery.hasNextPage,
        isFetchingNextPage: infiniteQuery.isFetchingNextPage,
        dataPages: infiniteQuery.data?.pages.length,
        status: infiniteQuery.status,
        fetchNextPage: typeof infiniteQuery.fetchNextPage,
        totalItems: allItems.length,
      });
    }
  }, [
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.data?.pages.length,
    infiniteQuery.status,
    infiniteQuery.fetchNextPage,
    allItems.length,
  ]);

  // Filtreleme değiştiğinde sorguyu sıfırla
  useEffect(() => {
    // Sadece başlangıç değerlerinden farklı olduğunda çalıştır
    if (searchValue !== initialQuery || sortOption !== initialSort) {
      logger.debug('Filtre değişti:', { searchValue, sortOption });

      // Sorguyu geçersiz kıl ve yeniden çek
      queryClient.resetQueries({
        queryKey: [keys],
      });

      // Scroll pozisyonunu sıfırla
      if (scrollContainer === window) {
        window.scrollTo(0, 0);
      } else if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTo({ top: 0 });
      }
    }
  }, [searchValue, sortOption, initialQuery, initialSort, keys, scrollContainer, queryClient]);

  // A view toggle keeps the data; it only starts the reader at the top again.
  useEffect(() => {
    if (viewMode === initialView) return;
    if (scrollContainer === window) {
      window.scrollTo(0, 0);
    } else if (scrollContainer instanceof HTMLElement) {
      scrollContainer.scrollTo({ top: 0 });
    }
  }, [viewMode, initialView, scrollContainer]);

  // Filtre değiştiğinde sorguyu sıfırla
  useEffect(() => {
    // Burada sadece refetch değil, tamamen yeniden veri çekmeliyiz
    // Çünkü queryKey içinde selectedFilters var ve değiştiğinde
    // yeni bir sorgu oluşturulmalı
    const hasActiveFilters = Object.values(selectedFilters).some(values => values.length > 0);

    // Sadece aktif filtre varsa ve bağımlılıklar değiştiğinde çalıştır
    if (hasActiveFilters) {
      // Eğer aktif filtre varsa, sorguyu tamamen sıfırla ve ilk sayfadan başla
      queryClient.resetQueries({
        queryKey: [keys],
      });

      // Scroll pozisyonunu sıfırla
      if (scrollContainer === window) {
        window.scrollTo(0, 0);
      } else if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTo({ top: 0 });
      }
    }
  }, [selectedFilters, keys, scrollContainer, queryClient]);

  // Filtre değişikliklerini işle
  const handleFilterChange = (filterKey: string, values: string[]) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterKey]: values,
    }));
  };

  return (
    <CatalogPage
      headerContent={headerContent}
      catalogType={catalogType}
      items={allItems as CatalogItem[]}
      searchConfig={searchConfig}
      filterCategories={filterCategories}
      initialSortOption={sortOption}
      initialViewMode={viewMode}
      onSearchChange={setSearchValue}
      onSortChange={setSortOption}
      onViewModeChange={(mode: string) => setViewMode(mode as ViewMode)}
      isLoading={isLoading}
      isError={false}
      hasMore={infiniteQuery.hasNextPage}
      isFetchingNextPage={infiniteQuery.isFetchingNextPage}
      totalDocs={totalDocs}
      selectedFilters={selectedFilters}
      onFilterChange={handleFilterChange}
    />
  );
}

// Yardımcı fonksiyon: Birden fazla initialData'yı birleştir
function combineInitialData(dataArray: PaginatedResponse<CatalogItem>[]): PaginatedResponse<CatalogItem> {
  if (!dataArray || dataArray.length === 0) {
    return {
      docs: [],
      totalDocs: 0,
      limit: 0,
      totalPages: 0,
      page: 0,
      pagingCounter: 0,
      hasPrevPage: false,
      hasNextPage: false,
      prevPage: null,
      nextPage: null,
    };
  }

  // Null olmayan sonuçları filtrele
  const validData = dataArray.filter(data => data !== null);

  if (validData.length === 0) {
    return {
      docs: [],
      totalDocs: 0,
      limit: 0,
      totalPages: 0,
      page: 0,
      pagingCounter: 0,
      hasPrevPage: false,
      hasNextPage: false,
      prevPage: null,
      nextPage: null,
    };
  }

  // Tek bir veri varsa, doğrudan onu döndür
  if (validData.length === 1) {
    return validData[0];
  }

  // Birden fazla veri varsa, birleştir
  const allDocs = validData.flatMap(data => data.docs || []);
  const dedupedDocs = uniqueCatalogItems(allDocs);
  const combinedData = {
    docs: dedupedDocs,
    totalDocs: validData.reduce((sum, data) => sum + (data.totalDocs || 0), 0),
    limit: validData[0].limit,
    totalPages: Math.max(...validData.map(data => data.totalPages || 0)),
    page: validData[0].page,
    pagingCounter: validData[0].pagingCounter,
    hasPrevPage: validData.some(data => data.hasPrevPage),
    hasNextPage: validData.some(data => data.hasNextPage),
    prevPage: validData[0].prevPage,
    nextPage: validData[0].nextPage,
  };

  return combinedData;
}
